import type { FollowTrackingContext } from '../../hooks/useFollowState'
import type { WinningTicketTokens } from '../../branding'
import type { FeedItem } from '../../types/feedContract'

/** Legs a compact tile lists before collapsing the rest into "+N more legs". */
export const COMPACT_TICKET_LEG_LIMIT = 4

// The persona has no avatar of its own; a ticket glyph stands in until an
// operator sets a category icon.
const DEFAULT_PERSONA_AVATAR = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">'
  + '<circle cx="24" cy="24" r="24" fill="#0B1E3F"/>'
  + '<g transform="rotate(-20 24 24)" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linejoin="round">'
  + '<path d="M11 18.5a2 2 0 0 1 2-2h22a2 2 0 0 1 2 2v2.6a3 3 0 0 0 0 5.8v2.6a2 2 0 0 1-2 2H13a2 2 0 0 1-2-2v-2.6a3 3 0 0 0 0-5.8z"/>'
  + '</g>'
  + '<circle cx="34.5" cy="32.5" r="5.5" fill="#22C55E"/>'
  + '<path d="m31.9 32.6 1.9 1.9 3.3-3.6" fill="none" stroke="#FFFFFF" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>'
  + '</svg>',
)}`

/** Category icon, else the persona's avatar, else the built-in glyph. */
export function winningTicketPersonaAvatar(item: FeedItem, tokens: Pick<WinningTicketTokens, 'categoryIconUrl'>): string {
  return tokens.categoryIconUrl || item.creator?.avatar_url || DEFAULT_PERSONA_AVATAR
}

/**
 * Follow state for a ticket's bottom pill. It follows `cta.target`, never the
 * persona in `item.creator`, so it cannot reuse the header follow props.
 */
export interface WinningTicketFollowControls {
  /** Anonymous viewers (no identify token) never see a Follow CTA. */
  viewerIdentified: boolean
  isFollowing: (creatorId?: string | null, fallback?: boolean) => boolean
  isFollowPending: (creatorId?: string | null) => boolean
  onToggleFollow: (creatorId: string, currentlyFollowing: boolean, context: FollowTrackingContext) => void
}

interface FollowStateLike {
  isFollowing: (creatorId?: string | null, fallback?: boolean) => boolean
  isPending: (creatorId?: string | null) => boolean
  toggleFollow: (creatorId: string | null | undefined, fallbackFollowing: boolean, context: FollowTrackingContext) => Promise<void>
}


export function winningTicketFollowControls(
  followState: FollowStateLike,
  viewerIdentified: boolean,
): WinningTicketFollowControls {
  return {
    viewerIdentified,
    isFollowing: followState.isFollowing,
    isFollowPending: followState.isPending,
    onToggleFollow: (creatorId, currentlyFollowing, context) =>
      void followState.toggleFollow(creatorId, currentlyFollowing, context),
  }
}

// The label the server generates for a creator's ticket (winningTicketFollowLabel
// in apps/shared/pkg/services/winning_ticket_post_service.go).
const GENERATED_FOLLOW_LABEL = /^Follow(?: (@\S+))?$/

/**
 * The pill's label in the chrome's language: the server's generated
 * "Follow @Handle" reads "Seguir @Handle" in Spanish; any other label is the
 * operator's own and is shown as written.
 */
export function winningTicketFollowLabel(label: string | undefined, follow: string): string {
  const trimmed = label?.trim() || ''
  if (!trimmed) return follow
  const generated = GENERATED_FOLLOW_LABEL.exec(trimmed)
  if (!generated) return trimmed
  return generated[1] ? `${follow} ${generated[1]}` : follow
}

/** For renderers without follow state: the card renders with no pill. */
export const NO_WINNING_TICKET_FOLLOW: WinningTicketFollowControls = {
  viewerIdentified: false,
  isFollowing: (_creatorId, fallback = false) => fallback,
  isFollowPending: () => false,
  onToggleFollow: () => {},
}

/**
 * The CSS variable each per-card palette token may override, by the same
 * names the organization's winningTicket branding tokens map to. Tokens
 * outside this list (and anything but a hex or rgba color) are ignored, so a
 * payload can never put arbitrary CSS on the card.
 */
const TICKET_PALETTE_VARIABLES: Record<string, string> = {
  headerBackground: '--feed-component-winning-ticket-header-bg',
  headerIconColor: '--feed-component-winning-ticket-header-icon-color',
  bodyBackground: '--feed-component-winning-ticket-body-bg',
  textColor: '--feed-component-winning-ticket-text-color',
  oddsColor: '--feed-component-winning-ticket-odds-color',
  dividerColor: '--feed-component-winning-ticket-divider-color',
  ctaBackground: '--feed-component-winning-ticket-cta-bg',
  ctaColor: '--feed-component-winning-ticket-cta-color',
}

const TICKET_PALETTE_COLOR = /^(#[0-9a-fA-F]{6}|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d{1,3})\s*\))$/

/**
 * Inline CSS variables that put a card's team colors over the organization's
 * winning ticket tokens, or undefined when the item carries no usable palette.
 */
export function ticketPaletteStyle(item: Pick<FeedItem, 'ticket_palette'>): Record<string, string> | undefined {
  const tokens = item.ticket_palette?.tokens
  if (!tokens || typeof tokens !== 'object') return undefined
  const style: Record<string, string> = {}
  for (const [token, variable] of Object.entries(TICKET_PALETTE_VARIABLES)) {
    const value = tokens[token]
    if (typeof value === 'string' && TICKET_PALETTE_COLOR.test(value.trim())) {
      style[variable] = value.trim()
    }
  }
  return Object.keys(style).length > 0 ? style : undefined
}
